import { getClusterColor, getScoreColor } from 'utils/helpers';
import ClusterTooltip from 'components/ClusterTooltip';
import DefinitionTooltip from 'components/DefinitionTooltip';
import HealthStatTooltip from 'components/HealthStatTooltip';
import OverviewMetricCell from 'components/OverviewMetricCell';
import { theme as themeTokens } from 'utils/theme';
import { tooltips } from '@/copy';

export function HealthStatMetricCell({
    value,
    defaultLabel,
    healthStatKey,
    healthStats,
    isLoading,
    hasBorder = true,
    labelLines = 2,
    valueTextSize,
    labelSuffix = '',
}) {
    const stat = healthStatKey ? healthStats?.[healthStatKey] : null;
    // A row with no score still carries a real value, so fall back to the metric's
    // own name rather than labelling "8.5M" as "Insufficient Data".
    const rawScoreLabel = stat?.scoreLabel;
    const scoreLabel =
        rawScoreLabel === tooltips.healthStatsMissingScoreLabel
            ? null
            : rawScoreLabel;
    return (
        <OverviewMetricCell
            value={value}
            label={
                scoreLabel == null
                    ? defaultLabel
                    : `${scoreLabel}${labelSuffix}`
            }
            valueTextSize={valueTextSize}
            isLoading={isLoading}
            hasBorder={hasBorder}
            labelLines={labelLines}
            hoverColor={
                scoreLabel
                    ? getScoreColor(stat.score, false, healthStatKey)
                    : null
            }
            tooltipWrapper={
                scoreLabel
                    ? (c) => (
                          <HealthStatTooltip
                              metricLabel={healthStatKey}
                              label={scoreLabel}
                          >
                              {c}
                          </HealthStatTooltip>
                      )
                    : null
            }
        />
    );
}

// Cluster values arrive prefixed with their source — "tiktok sustained riser". The
// tooltip copy is keyed on that full value (each source has its own definition, and
// "<source> no cluster" has no unprefixed twin), so only the label drops the prefix.
const clusterLabel = (cluster) =>
    cluster?.replace(/^(?:tiktok|instagram) /, '');

export function ClusterMetricCell({
    cluster,
    dataSource,
    isLoading,
    hasBorder = false,
    labelLines = 2,
}) {
    return (
        <OverviewMetricCell
            value={clusterLabel(cluster) || '—'}
            label="Cluster"
            valueTextSize="xs"
            uppercase={false}
            capitalize
            hasBorder={hasBorder}
            labelLines={labelLines}
            isLoading={isLoading}
            hoverColor={getClusterColor(cluster)}
            tooltipWrapper={
                cluster
                    ? (c) => (
                          <ClusterTooltip
                              cluster={cluster}
                              dataSource={dataSource}
                          >
                              {c}
                          </ClusterTooltip>
                      )
                    : null
            }
        />
    );
}

export function GrowthMetricCell({
    metric,
    value,
    label = 'YoY',
    isLoading,
    hasBorder = true,
    labelLines = 2,
    valueTextSize,
}) {
    const tooltip = metric ? tooltips[metric.tooltipType] : null;
    return (
        <OverviewMetricCell
            value={metric?.format(value) ?? '—'}
            label={label}
            valueTextSize={valueTextSize}
            isLoading={isLoading}
            hasBorder={hasBorder}
            labelLines={labelLines}
            hoverColor={
                value > 0
                    ? themeTokens.green_600
                    : value < 0
                      ? themeTokens.red_500
                      : null
            }
            tooltipWrapper={
                tooltip
                    ? (c) => (
                          <DefinitionTooltip
                              {...tooltip}
                              mixpanelMeta={{
                                  contentKey: metric.mixpanelContentKey,
                              }}
                          >
                              {c}
                          </DefinitionTooltip>
                      )
                    : null
            }
        />
    );
}

export default HealthStatMetricCell;
